import { useState, forwardRef } from "react";
import { motion } from "framer-motion";
import SearchInput from "@/components/SearchInput";

const CATEGORY_SUGGESTIONS: Record<string, string[]> = {
  monitor: [
    "Best laptop under $1000",
    "Best desk for dual monitors",
    "Best webcam for remote work",
    "Best monitor arm under $100",
  ],
  chair: [
    "Best standing desk under $500",
    "Best monitor for work from home",
    "Best desk lamp for office",
    "Best footrest for office chair",
  ],
  headphones: [
    "Best portable speaker under $100",
    "Best earbuds for working out",
    "Best DAC under $200",
    "Best headphone stand",
  ],
  earbuds: [
    "Best noise cancelling headphones",
    "Best portable speaker under $100",
    "Best wireless charger",
  ],
  vacuum: [
    "Best robot vacuum under $400",
    "Best air purifier for allergies",
    "Best mop for hardwood floors",
  ],
  "espresso machine": [
    "Best coffee grinder under $200",
    "Best milk frother",
    "Best coffee beans for espresso",
  ],
  "air fryer": [
    "Best instant pot under $100",
    "Best blender for smoothies",
    "Best toaster oven",
  ],
  tv: [
    "Best soundbar under $300",
    "Best streaming device",
    "Best TV mount",
  ],
  laptop: [
    "Best external monitor under $300",
    "Best laptop bag for travel",
    "Best wireless mouse",
  ],
};

const GLOBAL_SUGGESTIONS = [
  "Best office chair Canada",
  "Best espresso machine under €400 Germany",
  "Best air fryer under $150",
  "Best headphones for travel UK",
  "Best protein powder delivered tomorrow UAE",
];

interface Props {
  onSelect: (query: string) => void;
  category?: string;
}

const PostResultLoop = forwardRef<HTMLDivElement, Props>(function PostResultLoop({ onSelect, category }, ref) {
  const [showAll, setShowAll] = useState(false);

  // Find category-specific suggestions, falling back to global
  const categoryKey = category?.toLowerCase() || "";
  const matched = Object.entries(CATEGORY_SUGGESTIONS).find(([key]) =>
    categoryKey.includes(key) || key.includes(categoryKey)
  );
  const suggestions = matched ? matched[1] : GLOBAL_SUGGESTIONS;
  const visibleSuggestions = showAll ? suggestions : suggestions.slice(0, 3);

  return (
    <div ref={ref} className="text-center mt-16">
      <p className="text-lg font-display font-semibold text-foreground mb-6">
        Want another decision?
      </p>
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {visibleSuggestions.map((s) => (
          <motion.button
            key={s}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onSelect(s)}
            className="px-5 py-2.5 rounded-xl border border-border bg-secondary text-secondary-foreground font-body text-sm hover:border-primary/40 hover:bg-primary/5 transition-all duration-200"
          >
            {s}
          </motion.button>
        ))}
        {!showAll && suggestions.length > 3 && (
          <button
            onClick={() => setShowAll(true)}
            className="px-5 py-2.5 rounded-xl border border-border/40 text-muted-foreground font-body text-sm hover:text-foreground transition-colors"
          >
            Show more
          </button>
        )}
      </div>
      <SearchInput onSubmit={onSelect} isLoading={false} />
    </div>
  );
});

export default PostResultLoop;
